import PropTypes from 'prop-types';

import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import { Button as ClassityButton } from '@classity-edtech/ui-base';
import { Check, CloseIcon, FullLogo } from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { FormattedMessage } from 'react-intl';
import { useGetPaymentQuery } from '../../services/payment';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Helmet from '../../components/Helmet';

export const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('paymentId');
  const status = searchParams.get('status') || 'success';
  const transactionIdParam = searchParams.get('transactionId');

  const { data: payment } = useGetPaymentQuery({
    id: paymentId,
  });

  const transactionId = transactionIdParam || 'N/A';
  const orderId = payment?.order?.uid;
  const time = dayjs(payment?.meta?.created).format('DD MMM YYYY, hh:mm A');
  const paymentMethod = payment?.paymentServiceProvider?.paymentMethod;
  const amount = payment?.order?.totalAmount;
  const orderCurrency = payment?.order?.currencyCode;

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  const handleNextAction = () => {};

  return (
    <>
      <Helmet />
      <Container
        sx={{
          display: { xs: 'none', md: 'flex' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: { xs: 3, md: 0 },
          mx: { xs: 0, md: 'auto' },
        }}
      >
        <FullLogo
          sx={{
            height: { xs: '28px', md: '42px' },
            width: 'max-content',
          }}
          onClick={() => {}}
        />
        <CloseIcon sx={{ height: '14px', width: '14px' }} />
      </Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 4,
          px: { xs: 3, md: 5 },
          maxWidth: '600px',
          mx: 'auto',
        }}
      >
        <Container
          sx={{
            display: 'flex',
            py: { xs: 4, md: 2 },
            px: { xs: 1, md: 2 },
            boxShadow: { xs: '0px 0px 40px 0px #D9D9D9', md: 'none' },
            borderRadius: '16px',
            flexDirection: 'column',
            mx: { xs: 5, md: 'auto' },
            background: globalColours['$global-colour-background-elevated'],
            position: 'relative',
            marginTop: { xs: '25%', md: 0 },
          }}
        >
          <Stack
            sx={{
              pt: { xs: 2, md: 4 },
              gap: { xs: 2, md: 3 },
              alignItems: 'center',
              flex: 0.5,
              pb: { xs: 4, md: 0 },
              borderBottom: {
                xs: `dashed 1px ${globalColours['$global-colour-border-neutral']}`,
                md: 'none',
              },
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', md: 'column' },
                alignItems: 'center',
                gap: { xs: 2, md: 3 },
              }}
            >
              <Box>
                <Typography variant='subtitle3' sx={{ textAlign: 'center', display: 'block' }}>
                  Booking Confirmation
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ textAlign: 'center', display: { xs: 'none', md: 'flex' } }}
                >
                  Payment transaction completed. Check your email for further confirmation.
                </Typography>
              </Box>
              <Box
                sx={{
                  width: '56px',
                  height: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: status === 'success' ? '#0B891F1F' : 'rgba(217, 48, 18, 0.12)',
                  borderRadius: '50%',
                }}
              >
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      status === 'success'
                        ? globalColours['$global-colour-sentiment-positive-active']
                        : globalColours['$global-colour-sentiment-error-default'],
                    borderRadius: '50%',
                  }}
                >
                  {status === 'success' ? (
                    <Check
                      sx={{
                        width: '16px',
                        height: '16px',
                        color: globalColours['$global-colour-base-contrast'],
                      }}
                    />
                  ) : (
                    <CloseIcon
                      sx={{
                        width: '12px',
                        height: '12px',
                        color: globalColours['$global-colour-base-contrast'],
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            {status === 'success' && (
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <video
                  src='https://static04.classity.co/vid/success-animation.mp4'
                  controls={false}
                  loop
                  autoPlay
                  playsInline
                  muted
                  style={{ width: 'auto', height: '200px' }}
                />
              </Box>
            )}
            <Box
              sx={{
                width: '18px',
                height: '20px',
                borderRadius: '50%',
                background: globalColours['$global-colour-background-elevated'],
                position: 'absolute',
                bottom: '-10px',
                left: '-18px',
                display: { xs: 'block', md: 'none' },
              }}
            ></Box>
            <Box
              sx={{
                width: '18px',
                height: '20px',
                borderRadius: '50%',
                background: globalColours['$global-colour-background-elevated'],
                position: 'absolute',
                bottom: '-10px',
                right: '-18px',
                display: { xs: 'block', md: 'none' },
              }}
            ></Box>
          </Stack>
          <Box
            sx={{
              flex: 0.5,
            }}
          >
            <Stack
              sx={{
                pt: 4,
                pb: 3,
              }}
            >
              <Stack
                sx={{
                  gap: 2,
                  pb: 2,
                  mb: 2,
                  px: { xs: 2, md: 0 },
                  borderBottom: `dashed 1px ${globalColours['$global-colour-border-neutral']}`,
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Typography
                    variant='body3'
                    sx={{ color: globalColours['$global-colour-content-secondary'] }}
                  >
                    {localisedMessage('order.confirmation.transaction.id')}
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      textAlign: 'right',
                    }}
                  >
                    {transactionId || '_ _'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Typography
                    variant='body3'
                    sx={{ color: globalColours['$global-colour-content-secondary'] }}
                  >
                    {localisedMessage('order.confirmation.order.id')}
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      textAlign: 'right',
                    }}
                  >
                    {orderId || '_ _'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Typography
                    variant='body3'
                    sx={{ color: globalColours['$global-colour-content-secondary'] }}
                  >
                    {localisedMessage('time')}
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      textAlign: 'right',
                    }}
                  >
                    {time || '_ _'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <Typography
                    variant='body3'
                    sx={{ color: globalColours['$global-colour-content-secondary'] }}
                  >
                    {localisedMessage('order.confirmation.payment.method')}
                  </Typography>
                  <Typography
                    variant='body3SemiBold'
                    sx={{
                      color: globalColours['$global-colour-content-secondary'],
                      textAlign: 'right',
                      textTransform: 'capitalize',
                    }}
                  >
                    {paymentMethod?.toLowerCase() || '_ _'}
                  </Typography>
                </Box>
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                  pt: 2,
                  px: { xs: 2, md: 0 },
                }}
              >
                <Typography
                  variant='body3'
                  sx={{ color: globalColours['$global-colour-content-secondary'] }}
                >
                  {localisedMessage('amount')}
                </Typography>
                <Typography
                  variant='body3SemiBold'
                  sx={{
                    color: globalColours['$global-colour-content-secondary'],
                    textAlign: 'right',
                  }}
                >
                  {amount ? `${orderCurrency} ${amount?.toLocaleString()}` : '_ _'}
                </Typography>
              </Box>
            </Stack>

            {status === 'success' && (
              <ClassityButton
                variant='contained'
                data-testid={`make-payment-btn`}
                sx={{ height: '48px', width: 'fit', mx: 'auto' }}
                onClick={handleNextAction}
              >
                {localisedMessage('done')}
              </ClassityButton>
            )}
          </Box>
        </Container>

        {status === 'success' && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              position: 'absolute',
              bottom: '64px',
              zIndex: -1,
            }}
          >
            <video
              src='https://static04.classity.co/vid/success-animation.mp4'
              controls={false}
              loop
              autoPlay
              playsInline
              muted
              style={{ width: '200px', height: '200px' }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

OrderConfirmation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

OrderConfirmation.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(OrderConfirmation)(
  ({ theme }) => `
`,
);
